import type { PointsHistoryCategory, PointsHistoryItem, PointsHistoryRelatedTask } from '@/types';

import { formatPointsCount } from './pointsBalanceDisplay';

const refundPattern = /返还|退款|退回|释放|解冻|失败/;
const consumePattern = /消耗|扣减|扣除|冻结|支出/;

export const getPointsHistoryAmount = (item: PointsHistoryItem): number => {
  return Number(item.change_points || 0);
};

export const getPointsHistoryTitle = (item: PointsHistoryItem): string => {
  const explicitTitle = item.change_desc?.trim() || item.remark?.trim();
  if (explicitTitle) return explicitTitle;

  const category = getPointsHistoryCategory(item);
  if (category === 'refund') return '返还积分';
  if (category === 'consume') return '消耗积分';
  return '获得积分';
};

export const formatPointsHistoryAmount = (points: number): string => {
  const sign = points >= 0 ? '+' : '-';
  return `${sign}${formatPointsCount(Math.abs(points))}`;
};

export const formatPointsHistoryTime = (value: string): string => {
  if (!value) return '--';
  return value
    .replace('T', ' ')
    .replace(/\.\d+$/, '')
    .replace(/Z$/, '');
};

export const getPointsHistoryCategory = (item: PointsHistoryItem): PointsHistoryCategory => {
  const text = `${item.change_desc || ''} ${item.remark || ''}`;
  if (refundPattern.test(text)) return 'refund';
  if (consumePattern.test(text) || getPointsHistoryAmount(item) < 0) return 'consume';
  return 'earn';
};

export const getPointsHistoryCategoryLabel = (category: PointsHistoryCategory): string => {
  if (category === 'refund') return '返还';
  if (category === 'consume') return '消耗';
  if (category === 'earn') return '获得';
  return '全部';
};

const normalizeLabel = (value: string | null | undefined): string => String(value || '').trim();

const buildSessionKey = (userLabel: string | undefined, conversationKey: string): string => {
  const userId = normalizeLabel(userLabel);
  if (!userId || !/^\d+$/.test(userId) || !conversationKey) return '';
  return `agent:manclaw_user_${userId}:session:${conversationKey}`;
};

const formatSessionLabel = (sessionKey: string): string => {
  const normalized = normalizeLabel(sessionKey);
  if (!normalized) return '';
  const sessionMarker = ':session:';
  const sessionId = normalized.includes(sessionMarker)
    ? normalized.slice(normalized.lastIndexOf(sessionMarker) + sessionMarker.length)
    : normalized;
  return sessionId;
};

export const formatPointsHistoryProjectLabel = (task: PointsHistoryRelatedTask): string => {
  return normalizeLabel(task.project_name || task.project_title) || '--';
};

export const formatPointsHistorySessionLabel = (
  task: PointsHistoryRelatedTask,
  userLabel?: string,
): string => {
  return (
    formatSessionLabel(
      normalizeLabel(task.session_key || task.sessionKey) ||
        buildSessionKey(userLabel, normalizeLabel(task.conversation_key)),
    ) || '--'
  );
};

const summarizeTaskLabels = (
  tasks: PointsHistoryRelatedTask[] | undefined,
  formatter: (task: PointsHistoryRelatedTask) => string,
): string => {
  if (!tasks || tasks.length === 0) return '--';

  const labels = new Set<string>();
  tasks.forEach((task) => {
    const label = formatter(task);
    if (!label || label === '--') return;
    labels.add(label);
  });

  if (labels.size === 0) return '--';
  return Array.from(labels).join('、');
};

export const summarizePointsHistoryTaskProjects = (
  tasks: PointsHistoryRelatedTask[] | undefined,
): string => {
  return summarizeTaskLabels(tasks, formatPointsHistoryProjectLabel);
};

export interface PointsHistorySessionEntry {
  sessionId: string;
  title: string;
}

/** 会话标题截断约定为「前 20 字 + ...」，与后端 _auto_update_conversation_title 对齐 */
const SESSION_TITLE_MAX_LEN = 20;

/** 存量数据存在截断但缺省略号的情况，展示层统一补齐 */
export const normalizeSessionTitle = (title: string): string => {
  if (title.length >= SESSION_TITLE_MAX_LEN && !title.endsWith('...')) {
    return `${title}...`;
  }
  return title;
};

/** 按会话 ID 去重收集流水关联会话，保留首个非空标题，供表格两行展示 */
export const collectPointsHistoryTaskSessions = (
  tasks: PointsHistoryRelatedTask[] | undefined,
  userLabel?: string,
): PointsHistorySessionEntry[] => {
  if (!tasks || tasks.length === 0) return [];

  const entries = new Map<string, PointsHistorySessionEntry>();
  tasks.forEach((task) => {
    const sessionId = formatPointsHistorySessionLabel(task, userLabel);
    if (!sessionId || sessionId === '--') return;

    const title = normalizeSessionTitle(
      normalizeLabel(task.conversation_title || task.conversation_name),
    );
    const existing = entries.get(sessionId);
    if (!existing) {
      entries.set(sessionId, { sessionId, title });
      return;
    }
    if (!existing.title && title) {
      existing.title = title;
    }
  });

  return Array.from(entries.values());
};

export const filterPointsHistoryItems = (
  items: PointsHistoryItem[],
  category: PointsHistoryCategory,
): PointsHistoryItem[] => {
  if (category === 'all') return items;
  return items.filter((item) => getPointsHistoryCategory(item) === category);
};

const statusDisplayLabels: Record<string, string> = {
  SUCCESS: '成功',
  FAILED: '失败',
  RUNNING: '处理中',
  PENDING: '等待中',
};

export const formatPointsHistoryStatusSummary = (
  tasks: PointsHistoryRelatedTask[] | undefined,
): string[] => {
  if (!tasks || tasks.length === 0) return [];

  const statusCounts = new Map<string, number>();
  tasks.forEach((task) => {
    const normalized = task.status?.trim().toUpperCase();
    if (!normalized) return;
    statusCounts.set(normalized, (statusCounts.get(normalized) ?? 0) + 1);
  });

  const parts: string[] = [];
  for (const [status, count] of statusCounts) {
    const label = statusDisplayLabels[status] ?? status;
    parts.push(`${label}×${count}`);
  }

  return parts;
};
